import { RosterLevelEnum } from '@wwe-portal/entities/roster.type';
import {
  GemStats,
  SuperstarStats,
  SuperstarStatsSheet,
} from '@wwe-portal/entities/superstar-stats.type';
import {
  calculateSuperstarStats,
  calculateTotalTalent,
  findGemStats,
  getTotalHP,
  isSuperstarLevel,
  parseLevelOptionValue,
  parseSuperstarStarCode,
} from '@wwe-portal/web/utils/superstar-details/helper-v2';
import { getAvailableStarOptions } from '@wwe-portal/ui/features/roster/roster-star-select';
import { createContext, useContext, useMemo } from 'react';
import { createStore, StoreApi, useStore } from 'zustand';
import { UserSuperstarAbility } from '@wwe-portal/ui/features/roster/roster-ability-v2';
import { safeBound } from '@wwe-portal/ui/lib/math';
import { MAX_NUMBER_OF_ABILITIES } from '@wwe-portal/web/utils/superstar-details/constants';
import { wait } from '@wwe-portal/ui/lib/timer';
import { MySuperstarResponse } from '@wwe-portal/entities/superstar.type';

interface EditableStats {
  selectedStar: string;
  selectedStarColor: RosterLevelEnum;
  selectedLevel: number;
  selectedAbilities: UserSuperstarAbility[];
}

interface FullStats extends EditableStats {
  gemStats: GemStats | undefined;
  totalTalent: number | undefined;
  totalHP: number | undefined;
  levelCap: number | undefined;
}

const COMPARE_EFFECT_DURATION = 500;

interface SuperstarDetailContextValue {
  statsSheet: SuperstarStatsSheet;
  currentStats: FullStats;
  baseStats: FullStats | undefined;

  // Compare Effect
  compareEffect: {
    isActive: boolean;
    from: 'left' | 'right';
    duration: number;
    timestamp: number;
  };

  setBaseStats: () => void;
  clearCompareEffect: () => void;
  startCompareEffect: (from: 'left' | 'right') => Promise<void>;
  stopCompareEffect: () => void;
  resetCompareStats: () => void;
  resetToDefault: () => void;
  changeStar: (star: string) => void;
  changeStarColor: (starColor: RosterLevelEnum) => void;
  changeLevel: (level: number) => void;
  changeAbility: ({
    level,
    equipped,
    abilityId,
  }: {
    level: number;
    equipped: boolean;
    abilityId: string;
  }) => void;
}

const calculateFullStats = (
  statsSheet: SuperstarStatsSheet,
  currentEditableStats: EditableStats
) => {
  const localSuperstarStats = calculateSuperstarStats(statsSheet, {
    currentStar: currentEditableStats.selectedStar,
    currentStarColor: currentEditableStats.selectedStarColor,
  });
  const selectedAbilities = currentEditableStats.selectedAbilities;
  const superstarStatsPerLevel = findGemStats(
    localSuperstarStats,
    currentEditableStats.selectedLevel
  );
  const gemStats = superstarStatsPerLevel?.stats ?? undefined;
  const totalHP = getTotalHP(superstarStatsPerLevel);
  const totalTalent = calculateTotalTalent(
    localSuperstarStats,
    currentEditableStats.selectedLevel,
    selectedAbilities
  );

  const fullStatsResult: FullStats = {
    selectedStar: currentEditableStats.selectedStar,
    selectedStarColor: currentEditableStats.selectedStarColor,
    selectedLevel: safeBound(
      currentEditableStats.selectedLevel,
      1,
      localSuperstarStats?.LevelCap ?? 1
    ),
    selectedAbilities: selectedAbilities,
    gemStats,
    totalTalent,
    totalHP,
    levelCap: localSuperstarStats?.LevelCap,
  };

  return fullStatsResult;
};

const nextLowerStarColor = {
  [RosterLevelEnum.B]: RosterLevelEnum.G,
  [RosterLevelEnum.S]: RosterLevelEnum.B,
  [RosterLevelEnum.G]: RosterLevelEnum.S,
};

const getNearestLowerStar = (currentStar: string) => {
  const { solidStars, hollowStars } = parseSuperstarStarCode(currentStar) ?? {
    solidStars: 1,
    hollowStars: 0,
  };
  const totalStars = solidStars + hollowStars;
  let nextHollowStars = hollowStars;
  let nextSolidStars = solidStars - 1;
  if (nextSolidStars > 0) {
    nextHollowStars += 1;
  } else {
    nextSolidStars = totalStars - 1;
    nextHollowStars = 0;
  }
  return parseLevelOptionValue({
    solidStars: nextSolidStars,
    hollowStars: nextHollowStars,
  });
};

const getNearestLowerRarity = ({
  currentStar,
  currentStarColor,
}: {
  currentStar: string;
  currentStarColor: RosterLevelEnum | undefined;
}) => {
  // If rarity is the lowest, return the lowest rarity
  if (
    !currentStarColor ||
    (currentStarColor === RosterLevelEnum.B && currentStar === '1_1')
  ) {
    return null;
  }

  const nextStarColor = nextLowerStarColor[currentStarColor];

  // If color is the lowest, reduce the star number by 1
  if (currentStarColor === RosterLevelEnum.B) {
    return {
      currentStar: getNearestLowerStar(currentStar),
      currentStarColor: nextStarColor,
    };
  }

  return {
    currentStar: currentStar,
    currentStarColor: nextStarColor,
  };
};

const calculateEditableStats = (
  statsSheet: SuperstarStatsSheet,
  defaultValue: EditableStats,
  options?: {
    shouldAdjustAbilityLevel?: boolean;
    shouldAdjustSuperstarLevel?: boolean;
  }
) => {
  const shouldAdjustAbilityLevel = options?.shouldAdjustAbilityLevel ?? false;
  const shouldAdjustSuperstarLevel =
    options?.shouldAdjustSuperstarLevel ?? false;

  const selectedStar = defaultValue.selectedStar;
  const selectedStarColor = defaultValue.selectedStarColor;

  const superstarStats = calculateSuperstarStats(statsSheet, {
    currentStar: selectedStar,
    currentStarColor: selectedStarColor,
  });

  const nearestLowerRarity = getNearestLowerRarity({
    currentStar: selectedStar,
    currentStarColor: selectedStarColor,
  });

  const nearestLowerStats = nearestLowerRarity
    ? calculateSuperstarStats(statsSheet, nearestLowerRarity)
    : null;

  const selectedLevel = safeBound(
    shouldAdjustSuperstarLevel
      ? (nearestLowerStats?.LevelCap ?? 1)
      : defaultValue.selectedLevel,
    1,
    superstarStats?.LevelCap ?? 1
  );

  const currentAbilities = defaultValue?.selectedAbilities;

  const defaultAbilityLevel = 1;
  const selectedAbilities: UserSuperstarAbility[] =
    superstarStats?.Abilities.map((item, index) => {
      const currentAbility = currentAbilities?.find(
        (ability) => ability.localId === item.ID
      );
      const shouldEquiqWhenNoDefaultAbility = index < MAX_NUMBER_OF_ABILITIES;
      const levelCap = item.levelCap;
      const level = safeBound(
        currentAbility?.level ?? defaultAbilityLevel,
        1,
        levelCap
      );
      return {
        localId: item.ID,
        equipped: currentAbility?.equipped ?? shouldEquiqWhenNoDefaultAbility,
        level,
        levelCap,
        movePoint: item.MovePoint,
        name: item.name,
        details: item.levels[level - 1],
        gemType: item.gemType,
      };
    }) ?? [];

  const preferAbilityLevel = shouldAdjustAbilityLevel
    ? nearestLowerStats?.Abilities[0]?.levelCap
    : undefined;

  return {
    levelCap: superstarStats?.LevelCap,
    selectedStar,
    selectedStarColor,
    selectedAbilities: shouldAdjustAbilityLevel
      ? resetAbilities(selectedAbilities, preferAbilityLevel)
      : selectedAbilities,
    selectedLevel,
  };
};

const resetAbilities = (
  selectedAbilities: UserSuperstarAbility[],
  preferLevel: number | undefined
) => {
  return selectedAbilities.map((item, index) => {
    return {
      ...item,
      equipped: index < MAX_NUMBER_OF_ABILITIES,
      level: safeBound(preferLevel ?? 1, 1, item.levelCap ?? 1),
    };
  });
};

const calculateAbilityDetails = (
  superstarStats: SuperstarStats | undefined,
  {
    level,
    equipped,
    abilityId,
  }: {
    level: number;
    equipped: boolean;
    abilityId: string;
  }
) => {
  if (!superstarStats) return undefined;
  const abilityDetails = superstarStats.Abilities.find(
    (item) => item.ID === abilityId
  );
  if (!abilityDetails) return undefined;
  const details = abilityDetails?.levels[level - 1];
  const result: UserSuperstarAbility = {
    details,
    movePoint: abilityDetails?.MovePoint,
    localId: abilityDetails.ID,
    equipped,
    level,
    levelCap: abilityDetails?.levelCap,
    name: abilityDetails?.name ?? '',
    gemType: abilityDetails?.gemType ?? '',
  };
  return result;
};

const parseUserSuperstarResponseToEditableStats = (
  userSuperstar: MySuperstarResponse | undefined,
  statsSheet: SuperstarStatsSheet
) => {
  if (!userSuperstar)
    return {
      selectedStar: '1_1',
      selectedStarColor: RosterLevelEnum.B,
      selectedLevel: 1,
      selectedAbilities: [],
    };
  const starColor =
    userSuperstar.data.rarityTier &&
    isSuperstarLevel(userSuperstar.data.rarityTier)
      ? userSuperstar.data.rarityTier
      : RosterLevelEnum.B;

  const { solidStars } = userSuperstar.data;
  // Snap to the nearest fully-filled tier at or below the user's solid count
  // (e.g. 3 solid + 1 hollow → '3_3'), since the dropdown only exposes N_N options.
  const fullyFilledStars = Math.max(solidStars ?? 1, 1);
  const selectedStar = parseLevelOptionValue({
    solidStars: fullyFilledStars,
    hollowStars: 0,
  });
  const currentStats = calculateSuperstarStats(statsSheet, {
    currentStar: selectedStar,
    currentStarColor: starColor,
  });

  const abilities = userSuperstar.data.equippedAbilities
    ? (currentStats?.Abilities.map((item) => {
        const ability = userSuperstar.data.equippedAbilities.find(
          (currentItem) => currentItem.id === item.ID
        );
        return calculateAbilityDetails(currentStats, {
          level: ability?.level ?? 1,
          equipped: ability != null,
          abilityId: item.ID,
        });
      }).filter((item): item is UserSuperstarAbility => item != null) ?? [])
    : [];

  const result: EditableStats = {
    selectedStar,
    selectedStarColor: starColor,
    selectedLevel: userSuperstar.data.superstarLevel ?? 1,
    selectedAbilities: abilities,
  };
  return result;
};

const createSuperstarStore = (
  statsSheet: SuperstarStatsSheet,
  mySuperstarResponse: MySuperstarResponse | undefined
) => {
  const defaultValue = parseUserSuperstarResponseToEditableStats(
    mySuperstarResponse,
    statsSheet
  );

  return createStore<SuperstarDetailContextValue>((set, get) => {
    const initialStats = calculateFullStats(
      statsSheet,
      calculateEditableStats(statsSheet, defaultValue)
    );
    const value: SuperstarDetailContextValue = {
      statsSheet,
      currentStats: initialStats,
      baseStats: initialStats,
      compareEffect: {
        isActive: false,
        from: 'left',
        duration: 0,
        timestamp: 0,
      },
      resetToDefault: () => {
        set({
          currentStats: initialStats,
          baseStats: initialStats,
        });
      },
      setBaseStats: () => {
        set((state) => ({
          baseStats: state.currentStats,
        }));
        get().startCompareEffect('left');
      },
      clearCompareEffect: () => {
        set({
          compareEffect: {
            isActive: false,
            from: 'left',
            duration: 0,
            timestamp: 0,
          },
        });
      },
      startCompareEffect: async (from) => {
        const timestamp = Date.now();
        set({
          compareEffect: {
            isActive: true,
            from,
            duration: COMPARE_EFFECT_DURATION,
            timestamp: timestamp,
          },
        });
        await wait(COMPARE_EFFECT_DURATION);
        const currentTimestamp = get().compareEffect.timestamp;
        if (currentTimestamp !== timestamp) return;

        set({
          compareEffect: {
            isActive: false,
            from: 'left',
            duration: 0,
            timestamp: 0,
          },
        });
      },
      stopCompareEffect: () => {
        set(() => ({
          compareEffect: {
            isActive: false,
            from: 'left',
            duration: 0,
            timestamp: 0,
          },
        }));
      },
      resetCompareStats: () => {
        const nextStats =
          get().baseStats ??
          calculateFullStats(
            statsSheet,
            calculateEditableStats(statsSheet, defaultValue)
          );
        set({
          baseStats: nextStats,
          currentStats: nextStats,
        });
        get().startCompareEffect('right');
      },
      changeStar: (star: string) => {
        set((state) => ({
          currentStats: calculateFullStats(
            statsSheet,
            calculateEditableStats(
              statsSheet,
              {
                ...state.currentStats,
                selectedStar: star,
              },
              {
                shouldAdjustAbilityLevel: true,
                shouldAdjustSuperstarLevel: true,
              }
            )
          ),
        }));
      },
      changeStarColor: (starColor: RosterLevelEnum) => {
        set((state) => {
          const allowedStars = getAvailableStarOptions(starColor);
          const currentStar = state.currentStats.selectedStar;
          const nextStar = allowedStars.includes(currentStar)
            ? currentStar
            : (allowedStars[allowedStars.length - 1] ?? currentStar);
          return {
            currentStats: calculateFullStats(
              statsSheet,
              calculateEditableStats(
                statsSheet,
                {
                  ...state.currentStats,
                  selectedStar: nextStar,
                  selectedStarColor: starColor,
                },
                {
                  shouldAdjustAbilityLevel: true,
                  shouldAdjustSuperstarLevel: true,
                }
              )
            ),
          };
        });
      },
      changeLevel: (level: number) => {
        set((state) => ({
          currentStats: calculateFullStats(
            statsSheet,
            calculateEditableStats(
              statsSheet,
              {
                ...state.currentStats,
                selectedLevel: level,
              },
              {
                shouldAdjustAbilityLevel: false,
                shouldAdjustSuperstarLevel: false,
              }
            )
          ),
        }));
      },
      changeAbility: ({ level, equipped, abilityId }) => {
        const superstarStats = calculateSuperstarStats(statsSheet, {
          currentStar: get().currentStats.selectedStar,
          currentStarColor: get().currentStats.selectedStarColor,
        });
        const abilityDetails = calculateAbilityDetails(superstarStats, {
          level,
          equipped,
          abilityId,
        });
        if (!abilityDetails) return;
        const nextSelectedAbilities = get().currentStats.selectedAbilities.map(
          (item) =>
            item.localId === abilityId ? { ...item, ...abilityDetails } : item
        );
        set((state) => ({
          currentStats: calculateFullStats(statsSheet, {
            ...state.currentStats,
            selectedAbilities: nextSelectedAbilities,
          }),
        }));
      },
    };
    return value;
  });
};

const SuperstarDetailContext = createContext<
  StoreApi<SuperstarDetailContextValue> | undefined
>(undefined);

export const SuperstarDetailProvider = ({
  children,
  statsSheet,
  userSuperstar,
}: {
  children: React.ReactNode;
  statsSheet: SuperstarStatsSheet;
  userSuperstar: MySuperstarResponse | undefined;
}) => {
  const value = useMemo(
    () => createSuperstarStore(statsSheet, userSuperstar),
    [statsSheet, userSuperstar]
  );

  return (
    <SuperstarDetailContext.Provider value={value}>
      {children}
    </SuperstarDetailContext.Provider>
  );
};

export const useSuperstarDetailContext = <T,>(
  selector: (state: SuperstarDetailContextValue) => T
) => {
  const context = useContext(SuperstarDetailContext);
  if (!context) {
    throw new Error(
      'useSuperstarDetailContext must be used within a SuperstarDetailProvider'
    );
  }
  return useStore(context, selector);
};

export const useSuperstarDetailId = () => {
  return useSuperstarDetailContext((state) => state.statsSheet.id);
};
